import React, {
  type FC,
  type MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { Tournament } from '@/api/cms/tournaments';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { CountdownWidget } from '@/components/tournaments';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { getTimeLeft } from '@/helpers/timer';
import { getPlayerTournamentPoints } from '@/helpers/tournaments';
import { useGetCommonStore } from '@/store';
import { ScTournamentInfo } from './styled';

interface TournamentInfoSlideProps extends Tournament {
  setTournamentBannerCollapsed: (arg: boolean) => void;
  onClick: () => void;
}

export const TournamentInfoSlide = React.memo(
  ({
    id,
    banner: { minBet, prizePool, durationTo },
    icon,
    backendTournament,
    onClick,
    setTournamentBannerCollapsed,
  }: TournamentInfoSlideProps) => {
    const { t } = useTranslation('common');
    const timeLeft = useMemo(() => getTimeLeft(durationTo), [durationTo]);
    const useCommonStore = useGetCommonStore();

    const isMobileDevice = useCommonStore.use.isMobileDevice();

    const tournamentInfoSlideContent = useMemo(
      () => (
        <>
          {prizePool && (
            <p>
              {prizePool}
              <span> {t('tournaments.prizePool')}</span>
            </p>
          )}
          {minBet && (
            <p>
              {minBet}
              <span> {t('tournaments.minBet')}</span>
            </p>
          )}
        </>
      ),
      [prizePool, minBet, t]
    );

    const collapseBanner: MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        if (isMobileDevice) {
          e.stopPropagation();
          setTournamentBannerCollapsed(true);
        }
      },
      [isMobileDevice, setTournamentBannerCollapsed]
    );

    return (
      <ScTournamentInfo isMobile={isMobileDevice} key={id} onClick={onClick}>
        <ImageComponent
          alt="cup"
          height={32}
          src={icon?.url || '/cup.webp'}
          width={32}
        />
        <div className="slider">
          <div className="info-slide">{tournamentInfoSlideContent}</div>
          <div className="info-slide">
            <UsersStatusSlide backendTournament={backendTournament} />
          </div>
          <div className="info-slide">
            <CountdownWidget timeLeft={timeLeft} />
          </div>
        </div>
        <div className="arrow" onClick={collapseBanner}>
          <IconComponent
            className="arrow-icon"
            name="whiteArrowRight"
            size="20px"
          />
        </div>
      </ScTournamentInfo>
    );
  }
);

const UsersStatusSlide: FC<Pick<Tournament, 'backendTournament'>> = ({
  backendTournament,
}) => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();

  const userTournamentsStatuses = useCommonStore.use.userTournamentsStatuses();

  const userAwardPlace = useMemo(
    () =>
      userTournamentsStatuses?.find(
        ({ tournamentId }) => tournamentId === Number(backendTournament?.id)
      )?.awardPlace || 0,
    [backendTournament?.id, userTournamentsStatuses]
  );

  const [points, prizePool] = useMemo(() => {
    if (!backendTournament) return [0, 0];

    const { topPlayers, prizes, currency, strategy } = backendTournament;

    const currentPrize = prizes.find(
      (prize) => prize.awardPlace === userAwardPlace
    );

    const userTopPlayer = topPlayers.find(
      ({ awardPlace }) => awardPlace === userAwardPlace
    );

    const totalAward = currentPrize
      ? (({ moneyAward, freespinsCount }) => {
          if (freespinsCount === 0) {
            const roundedMoneyAward = Math.round(Number(moneyAward));
            return amountWithCurrencySymbol(currency, roundedMoneyAward);
          }

          return `${freespinsCount} FS`;
        })(currentPrize)
      : 0;

    const points = Math.round(
      getPlayerTournamentPoints(strategy, userTopPlayer)
    );

    return [points, totalAward];
  }, [userAwardPlace, backendTournament]);

  return (
    <>
      <p>
        {userAwardPlace}
        <span> {t('tournaments.place')}</span>
      </p>
      <p>
        {points}
        <span> {t('tournaments.points')}</span>
      </p>
      <p>
        {prizePool}
        <span> {t('tournaments.prize')}</span>
      </p>
    </>
  );
};
